import { format } from '@e3dc-react/shell/Libs/date'
import { BatteryHealthStatusDto } from '../api/dto/battery-status.dto'
import { BatteryHealthStatus, BatteryHealthStatusItem } from '../models'
import i18next, { TFunction } from 'i18next'

const NOT_AVAILABLE = 'N/A'

export const mergeBatteryHealthStatusItems = (
  batteryHealthEntries: Array<BatteryHealthStatusItem | null>
): Record<keyof BatteryHealthStatus, Array<string | number>> => {
  return batteryHealthEntries.reduce((groupedObject, entry) => {
    if (!entry) return groupedObject

    Object.keys(entry).forEach(key => {
      const typedKey = key as keyof BatteryHealthStatus

      if (!groupedObject[typedKey]) {
        groupedObject[typedKey] = []
      }
      groupedObject[typedKey].push(entry[typedKey])
    })
    return groupedObject
  }, {} as Record<keyof BatteryHealthStatus, Array<string | number>>)
}

export const fromHealthStatusDtoToModelTransformer = (dto: BatteryHealthStatusDto | null, t: TFunction): BatteryHealthStatusItem | null => {
  if (!dto) return null

  const dateOfCommissioning =
    dto.battvoFullEquivalentChargeDischargeCycles > 5 || !dto.battvoDatePuttingIntoService
      ? NOT_AVAILABLE
      : format(new Date(dto.battvoDatePuttingIntoService), t('common.format.date-fns-date'))

  const dateOfManufacture =
    !dto.battvoDateOfManufacture || dto.battvoDateOfManufacture === ' '
      ? NOT_AVAILABLE
      : format(new Date(dto.battvoDateOfManufacture), t('common.format.date-fns-date'))

  return {
    batteryType: dto.battvoBattype,
    remainingCapacity: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoRemainingCapacity),
    numberOfCycles: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 0 }).format(dto.battvoFullEquivalentChargeDischargeCycles),
    capacityThroughput: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoCapacityThroughput),
    energyThroughput: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoEnergyThroughput),
    remainingPowerCapabilityCharge: dto.battvoRemainingPowerCapabilityCharge
      ? Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoRemainingPowerCapabilityCharge)
      : NOT_AVAILABLE,
    remainingPowerCapabilityDischarge: dto.battvoRemainingPowerCapabilityDischarge
      ? Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoRemainingPowerCapabilityDischarge)
      : NOT_AVAILABLE,
    remainingEfficiency: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoRemainingRoundTripEfficiency),
    ohmicResistance: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoOhmicResistance),
    selfDischargeRates: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoEvolutionOfSelfDischargeRates),
    dateOfManufacture: dateOfManufacture,
    dateOfCommissioning: dateOfCommissioning,
    harmfulEventsDeepDischargeEvents: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(
      dto.battvoHarmfulEventsDeepDischargeEvents
    ),
    harmfulEventsTimeTempExtremeHigh: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(
      dto.battvoHarmfulEventsTimeTempExtremeHigh
    ),
    harmfulEventsTimeTempExtremeHighCharge: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(
      dto.battvoHarmfulEventsTimeTempExtremeHighCharge
    ),
    harmfulEventsOverchargeEvents: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoHarmfulEventsOverchargeEvents),
    harmfulEventsTimeTempExtremeLowCharge: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(
      dto.battvoHarmfulEventsTimeTempExtremeLowCharge
    ),
    harmfulEventsTimeDeepDischarge: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(
      dto.battvoHarmfulEventsTimeDeepDischarge
    ),
    harmfulEventsTimeOvercharge: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(dto.battvoHarmfulEventsTimeOvercharge),
    harmfulEventsTimeOvercurrentDischarge: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(
      dto.battvoHarmfulEventsTimeOvercurrentDischarge
    ),
    harmfulEventsTimeTempExtremeLow: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(
      dto.battvoHarmfulEventsTimeTempExtremeLow
    ),
    harmfulEventsTimeOvercurrentCharge: Intl.NumberFormat(i18next.language, { maximumFractionDigits: 2 }).format(
      dto.battvoHarmfulEventsTimeOvercurrentCharge
    ),
  }
}
